<template>
  <div class="shipping-calculator-container">
    <!-- Título -->
    <h3 class="text-xs sm:text-sm md:text-base font-semibold">{{ $t('shipping.title') }}</h3>

    <!-- Loading Overlay -->
    <div v-if="loading" class="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center z-10">
      <p class="text-blue-500 font-medium text-sm">{{ $t('shipping.loading') }}...</p>
    </div>

    <!-- Estado e Cidade (se não estiver logado) -->
    <div v-if="!user && showAllOptions && !loading" class="flex flex-col gap-2 w-full">
      <select v-model="selectedState" @change="updateCities" class="input w-full">
        <option disabled value="">{{ $t('shipping.choose_state') }}</option>
        <option v-for="estado in estados_py" :key="estado.estado_id" :value="estado.estado_id">
          {{ estado.estado_nome }}
        </option>
      </select>

      <select v-model="selectedCity" :disabled="!selectedState" class="input w-full">
        <option disabled value="">{{ $t('shipping.choose_city') }}</option>
        <option v-for="cidade in cities" :key="cidade.cidade_id" :value="cidade.cidade_id">
          {{ cidade.nome_cidade }}
        </option>
      </select>
    </div>

    <!-- Endereço simplificado (se logado) -->
    <p v-else-if="user && user.enderecos?.length && !loading" class="address">
      {{ $t('shipping.using_user_address') }}:
      {{ user.enderecos[0].logradouro }}, {{ user.enderecos[0].cidade }}, {{ user.enderecos[0].estado }}
    </p>

    <!-- Opções de Entrega -->
    <div v-if="!loading && deliveryOptions.length">
      <!-- Lista de opções (expandido) -->
      <div v-if="showAllOptions" class="flex flex-col gap-2">
        <label v-for="option in deliveryOptions" :key="option.entrega_servico_id" class="option">
          <input type="radio" v-model="selectedDeliveryOption" :value="option" />
          {{ option.descricao }} - {{ formatPrice(option.valor) }}
        </label>
      </div>

      <!-- Opção selecionada (minimizado) -->
      <div v-else @click="toggleOptions"
        class="cursor-pointer flex items-center gap-2 p-2 border border-gray-200 rounded-lg hover:border-blue-500 transition-colors">
        <p class="text-sm text-gray-700">{{ selectedDeliveryOption.descricao }} - {{ formatPrice(selectedDeliveryOption.valor) }}</p>
        <i :class="['fas', showAllOptions ? 'fa-chevron-up' : 'fa-chevron-down', 'text-blue-500']"></i>
      </div>
    </div>

    <!-- Mensagem de erro -->
    <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
  </div>
</template>

<script>
import { calculateShipping } from '@/services/apiService';
import { estados_py } from './Cidades';
import { mapGetters, mapActions } from 'vuex';

export default {
  props: {
    isCheckout: { type: Boolean, default: false },
    cart: { type: Object, required: true },
  },
  data() {
    return {
      estados_py,
      selectedState: localStorage.getItem('selectedState') || '',
      selectedCity: localStorage.getItem('selectedCity') || '',
      cities: [],
      deliveryOptions: JSON.parse(localStorage.getItem('deliveryOptions')) || [],
      selectedDeliveryOption: JSON.parse(localStorage.getItem('selectedDeliveryOption')) || null,
      loading: false,
      errorMessage: '',
      lastCalculatedData: JSON.parse(localStorage.getItem('lastCalculatedData')) || null,
      abortController: null,
      isMounted: false,
      showAllOptions: true,
    };
  },
  computed: {
    ...mapGetters({ user: 'auth/getUser' }),

    canCalculate() {
      return this.user || (this.selectedState && this.selectedCity);
    },
    ...mapGetters(['selectedShippingOption', 'shippingCost']),

    currentCityId() {
      return this.user?.enderecos[0].cidade_id || this.selectedCity;
    },

    currentCalculationData() {
      return {
        cart: this.cart,
        cityId: this.currentCityId,
      };
    },
  },
  watch: {
    currentCalculationData: {
      handler(newData) {
        if (this.canCalculate && this.hasCalculationDataChanged(newData)) {
          this.calculateShipping();
        }
      },
      deep: true,
    },
    selectedState(newState) {
      localStorage.setItem('selectedState', newState);
      this.updateCities();
    },
    selectedCity(newCity) {
      localStorage.setItem('selectedCity', newCity);
    },
    selectedDeliveryOption(newOption) {
      localStorage.setItem('selectedDeliveryOption', JSON.stringify(newOption));
      if (newOption) {
        this.$emit('shipping-selected', {
          entrega_integrador_id: newOption.entrega_integrador_id,
          entrega_servico_id: newOption.entrega_servico_id,
          valor: newOption.valor,
        });
        this.showAllOptions = false;
      }
    },
    user(newUser) {
      if (!newUser) {
        localStorage.removeItem('selectedState');
        localStorage.removeItem('selectedCity');
        localStorage.removeItem('selectedDeliveryOption');
        localStorage.removeItem('deliveryOptions');
        localStorage.removeItem('lastCalculatedData');
        this.selectedState = '';
        this.selectedCity = '';
        this.selectedDeliveryOption = null;
        this.deliveryOptions = [];
        this.lastCalculatedData = null;
      }
    },
  },
  methods: {
    async calculateShipping() {
      if (!this.isMounted) return;

      this.loading = true;
      this.errorMessage = '';

      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();

      try {
        const cityId = this.currentCityId;
        const { deliveryOptions } = await calculateShipping(this.cart, cityId, {
          signal: this.abortController.signal,
        });

        if (this.isMounted) {
          this.deliveryOptions = deliveryOptions;
          localStorage.setItem('deliveryOptions', JSON.stringify(deliveryOptions));

          if (this.selectedDeliveryOption) {
            const selectedOption = deliveryOptions.find(
              option =>
                option.entrega_integrador_id === this.selectedDeliveryOption.entrega_integrador_id &&
                option.entrega_servico_id === this.selectedDeliveryOption.entrega_servico_id
            );
            if (selectedOption) {
              this.selectedDeliveryOption = selectedOption;
            } else {
              this.selectedDeliveryOption = null;
            }
          }

          this.lastCalculatedData = { ...this.currentCalculationData };
          localStorage.setItem('lastCalculatedData', JSON.stringify(this.lastCalculatedData));

          this.$emit('shipping-updated', deliveryOptions);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.errorMessage = this.$t('shipping.error');
        }
      } finally {
        if (this.isMounted) {
          this.loading = false;
        }
      }
    },
    ...mapActions(['updateSelectedShippingOption', 'updateShippingCost']),
    emitSelectedDeliveryOption(option) {
      this.$emit('shipping-selected', option.valor);
    },
    updateCities() {
      const estado = this.estados_py.find(e => e.estado_id === this.selectedState);
      this.cities = estado ? estado.cidades : [];

      // Verifica se a cidade salva no localStorage existe na lista de cidades carregadas
      if (this.selectedCity && this.cities.length) {
        const cidadeSelecionada = this.cities.find(c => c.cidade_id === this.selectedCity);
        if (!cidadeSelecionada) {
          this.selectedCity = ''; // Reseta a cidade selecionada se não for válida
        }
      }
    },
    formatPrice(value) {
      return value > 0 ? `G$ ${value.toLocaleString('pt-BR')}` : this.$t('shipping.free_shipping');
    },
    hasCalculationDataChanged(newData) {
      return JSON.stringify(newData) !== JSON.stringify(this.lastCalculatedData);
    },
    toggleOptions() {
      this.showAllOptions = !this.showAllOptions;
    },
  },
  mounted() {
    this.isMounted = true;

    // Se houver um estado selecionado, carrega as cidades correspondentes
    if (this.selectedState) {
      this.updateCities();
    }

    // Se houver uma opção de entrega selecionada, minimiza as opções
    if (this.selectedDeliveryOption) {
      this.showAllOptions = false;
    }

    // Calcula o frete se necessário
    if (this.canCalculate && this.hasCalculationDataChanged(this.currentCalculationData)) {
      this.calculateShipping();
    }
  },
};
</script>

<style scoped>
.shipping-calculator-container {
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background-color: #ffffff;
}

.input {
  appearance: none;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.5rem;
  font-size: 0.875rem;
  outline: none;
  transition: border-color 0.2s;
  width: 100%; 
  margin: 5px
}

.input:focus {
  border-color: #3b82f6;
}

.address {
  font-size: 0.875rem;
  color: #374151;
  line-height: 1.4;
}

.option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  padding: 0.5rem;
  border-radius: 6px;
  transition: background-color 0.2s;
}

.option:hover {
  background-color: #f9fafb;
}

.error {
  color: #dc2626;
  font-size: 0.875rem;
  padding: 0.5rem;
  border-radius: 6px;
  background-color: #fef2f2;
}

.absolute {
  padding-top: 0;
}

.fa-chevron-down, .fa-chevron-up {
  transition: transform 0.2s;
}
</style>